/*
 * Part search for the Refresh theme (and other Dawn-family themes).
 *
 * 1. Predictive search dropdown: results come from the part-search Worker.
 * 2. Search results page (/search?q=...): results, filters, sorting and
 *    paging come from the Worker.
 *
 * Both fall back to Shopify's own search if the Worker fails or is slow.
 */
(() => {
  const cfg = window.PartSearchConfig;
  if (!cfg || !cfg.endpoint || cfg.endpoint.includes('YOUR-SUBDOMAIN')) {
    document.documentElement.classList.remove('part-search-loading');
    return;
  }

  const L = cfg.labels || {};
  const endpoint = cfg.endpoint.replace(/\/$/, '');
  const timeoutMs = cfg.timeoutMs || 3000;

  // ---------------------------------------------------------------- helpers

  const esc = (value) =>
    String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  const fill = (template, vars) =>
    String(template || '').replace(/\{(\w+)\}/g, (_, k) => (vars[k] !== undefined ? vars[k] : ''));

  let moneyFormat;
  try {
    moneyFormat = new Intl.NumberFormat(cfg.locale || undefined, { style: 'currency', currency: cfg.currency || 'GBP' });
  } catch {
    moneyFormat = null;
  }
  const money = (n) => (n == null ? '' : moneyFormat ? moneyFormat.format(n) : Number(n).toFixed(2));

  const sizedImage = (url, width) => {
    if (!url) return '';
    try {
      const u = new URL(url);
      u.searchParams.set('width', String(width));
      return u.toString();
    } catch {
      return url;
    }
  };

  // Fetch JSON from the Worker. Rejects on HTTP errors and on timeout;
  // rejects with name 'AbortError' only when the caller aborted.
  function api(params, callerSignal) {
    const url = new URL(endpoint + '/search');
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, v);
    });
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(new DOMException('timeout', 'TimeoutError')), timeoutMs);
    const onCallerAbort = () => controller.abort(new DOMException('aborted', 'AbortError'));
    if (callerSignal) callerSignal.addEventListener('abort', onCallerAbort, { once: true });

    return fetch(url.toString(), { signal: controller.signal, credentials: 'omit' })
      .then((res) => {
        if (!res.ok) throw new Error('Part search HTTP ' + res.status);
        return res.json();
      })
      .catch((err) => {
        if (callerSignal?.aborted) throw new DOMException('aborted', 'AbortError');
        throw err;
      })
      .finally(() => {
        clearTimeout(timer);
        if (callerSignal) callerSignal.removeEventListener('abort', onCallerAbort);
      });
  }

  // Merchant-written no-results message (HTML from the snippet); the search term is escaped.
  const noResultsHtml = (q) => fill(L.noResultsMessage || L.noResults || 'No results found for “{q}”', { q: esc(q) });
  const stripTags = (html) => html.replace(/<[^>]*>/g, '');

  // "RRC" -> "Showing results for Range Rover Classic"
  const aliasNote = (data) => {
    const targets = [...new Set((data.expanded || []).flatMap((e) => e.targets))];
    if (!targets.length) return '';
    return `<p class="part-search-note part-search-note--alias">${esc(fill(L.aliasNote || 'Showing results for {targets}', { targets: targets.join(', ') }))}</p>`;
  };

  const matchNote = (item) =>
    item.match && item.match.field === 'linked'
      ? `<span class="part-search-note">${esc(L.supersedes || 'Supersedes')} ${esc(item.match.value)}</span>`
      : '';

  // ------------------------------------------------------------- dropdown

  const arrowSvg =
    '<svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-arrow" viewBox="0 0 14 10" aria-hidden="true" focusable="false"><path fill="currentColor" fill-rule="evenodd" d="M8.537.808a.5.5 0 0 1 .817-.162l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 1 1-.708-.708L11.793 5.5H1a.5.5 0 0 1 0-1h10.793L8.646 1.354a.5.5 0 0 1-.109-.546" clip-rule="evenodd"/></svg>';

  function renderDropdown(data, term) {
    const items = data.results || [];
    const list = items
      .map(
        (item, i) => `
        <li id="predictive-search-option-product-${i + 1}" class="predictive-search__list-item" role="option" aria-selected="false">
          <a href="${esc(item.url)}" class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text" tabindex="-1">
            ${
              item.image || cfg.placeholderImage
                ? `<img class="predictive-search__image${item.image ? '' : ' part-search__thumb--placeholder'}" src="${esc(sizedImage(item.image || cfg.placeholderImage, 150))}" alt="" width="50" height="50" loading="lazy">`
                : ''
            }
            <div class="predictive-search__item-content predictive-search__item-content--centered">
              ${item.part_number ? `<p class="predictive-search__item-vendor caption-with-letter-spacing">${esc(item.part_number)}</p>` : ''}
              <p class="predictive-search__item-heading h5">${esc(item.title)}</p>
              ${matchNote(item)}
              <div class="price"><div class="price__container"><span class="price-item price-item--regular">${esc(money(item.price))}</span></div></div>
            </div>
          </a>
        </li>`
      )
      .join('');

    const groups = items.length
      ? `<div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--no-suggestions">
           <div class="predictive-search__result-group">
             <div>
               <h2 id="predictive-search-products" class="predictive-search__heading text-body caption-with-letter-spacing">${esc(L.products || 'Products')}</h2>
               ${aliasNote(data)}
               <ul id="predictive-search-results-products-list" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-products">${list}</ul>
             </div>
           </div>
         </div>`
      : '';

    const status = items.length
      ? fill(L.resultsCount || '{count} results for “{q}”', { count: items.length, q: esc(term) })
      : fill(L.noResults || 'No results found for “{q}”', { q: esc(term) });

    return `
      <div id="predictive-search-results" role="listbox">
        ${groups}
        ${items.length ? '' : `<div class="part-search__empty part-search__empty--dropdown">${fill(L.noResults || 'No results found for “{q}”', { q: esc(term) })}</div>`}
        <div id="predictive-search-option-search-keywords" class="predictive-search__search-for-button">
          <button class="predictive-search__item predictive-search__item--term link link--text h5 animate-arrow" tabindex="-1" role="option" aria-selected="false">
            <span data-predictive-search-search-for-text>${fill(L.searchFor || 'Search for “{q}”', { q: esc(term) })}</span>
            <span class="svg-wrapper">${arrowSvg}</span>
          </button>
        </div>
        <span class="hidden" data-predictive-search-live-region-count-value data-total-results="${items.length}">${status}</span>
      </div>`;
  }

  function patchPredictiveSearch() {
    const PredictiveSearch = customElements.get('predictive-search');
    if (!PredictiveSearch || PredictiveSearch.prototype.__partSearchPatched) return;
    const original = PredictiveSearch.prototype.getSearchResults;

    PredictiveSearch.prototype.getSearchResults = function (searchTerm) {
      this.__partSearchCache = this.__partSearchCache || new Map();
      if (this.__partSearchController) this.__partSearchController.abort();
      const key = searchTerm.trim().toLowerCase();

      if (this.__partSearchCache.has(key)) {
        this.renderSearchResults(this.__partSearchCache.get(key));
        return;
      }

      const controller = new AbortController();
      this.__partSearchController = controller;
      if (typeof this.setLiveRegionLoadingState === 'function') this.setLiveRegionLoadingState();

      api({ q: searchTerm, mode: 'suggest', limit: cfg.dropdownLimit || 8 }, controller.signal)
        .then((data) => {
          if (controller.signal.aborted || this.getQuery() !== searchTerm) return;
          const markup = renderDropdown(data, searchTerm);
          this.__partSearchCache.set(key, markup);
          this.renderSearchResults(markup);
        })
        .catch((err) => {
          if (err && err.name === 'AbortError') return; // superseded by a newer keystroke
          console.warn('[part-search] falling back to Shopify search:', err);
          original.call(this, searchTerm);
        });
    };
    PredictiveSearch.prototype.__partSearchPatched = true;
  }

  if (window.customElements) {
    customElements.whenDefined('predictive-search').then(patchPredictiveSearch);
  }

  // --------------------------------------------------------- results page

  const routesRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const cardCache = new Map();

  // Theme product card for one product, rendered by Shopify (correct currency,
  // sale badge, Add to cart). Resolves to null if it can't be fetched.
  function themeCard(item) {
    const handle = String(item.url || '').split('/products/')[1];
    if (!handle) return Promise.resolve(null);
    if (!cardCache.has(handle)) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 5000);
      cardCache.set(
        handle,
        fetch(`${routesRoot()}products/${encodeURIComponent(handle)}?view=ps-card`, { signal: controller.signal, credentials: 'same-origin' })
          .then((r) => (r.ok ? r.text() : null))
          .then((html) => (html && /class="[^"]*card/.test(html) && !/<html[\s>]/i.test(html) ? html.trim() : null))
          .catch(() => null)
          .finally(() => clearTimeout(timer))
      );
    }
    return cardCache.get(handle);
  }

  // Make sure the styles/scripts the theme cards need are on the page.
  let cardAssetsLoaded = false;
  function loadCardAssets() {
    if (cardAssetsLoaded) return;
    cardAssetsLoaded = true;
    const assets = cfg.themeCardAssets || {};
    const has = (url, attr) => [...document.querySelectorAll(`[${attr}]`)].some((el) => el.getAttribute(attr).split('?')[0] === url.split('?')[0]);
    (assets.css || []).forEach((href) => {
      if (!href || has(href, 'href')) return;
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      document.head.appendChild(link);
    });
    (assets.js || []).forEach((src) => {
      if (!src || has(src, 'src')) return;
      const script = document.createElement('script');
      script.src = src;
      script.defer = true;
      document.head.appendChild(script);
    });
  }

  function initResultsPage() {
    const release = () => document.documentElement.classList.remove('part-search-loading');
    if (cfg.resultsPage === false) return release();
    if (!/\/search\/?$/.test(location.pathname)) return release();

    const params = new URLSearchParams(location.search);
    const q = (params.get('q') || '').trim();
    const typeParam = params.get('type');
    if (!q || (typeParam && !typeParam.includes('product'))) return release();

    const wrapper = document.querySelector('.template-search');
    if (!wrapper) return release();

    const root = document.createElement('div');
    root.id = 'PartSearchResults';
    root.className = 'part-search page-width';
    const header = wrapper.querySelector('.template-search__header');
    if (header) header.after(root);
    else wrapper.prepend(root);

    const state = {
      q,
      page: Math.max(parseInt(params.get('ps_page'), 10) || 1, 1),
      vendor: params.get('ps_vendor') || '',
      brand: params.get('ps_brand') || '',
      discount: params.get('ps_discount') || '',
      type: params.get('ps_type') || '',
      sort: params.get('ps_sort') || '',
    };

    const html = document.documentElement;
    const nativeGrid = wrapper.querySelector('ul.product-grid, .product-grid');
    const gridClass = (nativeGrid && nativeGrid.tagName === 'UL' && nativeGrid.className) || cfg.gridClass || 'grid product-grid grid--2-col-tablet-down grid--3-col-desktop';
    let first = true;
    let inflight = null;

    async function load() {
      if (inflight) inflight.abort();
      const controller = new AbortController();
      inflight = controller;
      html.classList.add('part-search-loading');
      root.setAttribute('aria-busy', 'true');
      try {
        let data = await api(
          { q: state.q, mode: 'full', page: state.page, per_page: cfg.perPage || 24, vendor: state.vendor, brand: state.brand, type: state.type, discount: state.discount, sort: state.sort },
          controller.signal
        );
        const cards = cfg.themeCards === false ? null : await Promise.all(data.results.map(themeCard));
        if (controller.signal.aborted) return;
        render(data, cards);
        html.classList.add('part-search-active');
        if (!first) syncUrl();
        first = false;
      } catch (err) {
        if (err && err.name === 'AbortError') return;
        console.warn('[part-search] results page falling back to Shopify search:', err);
        if (first) {
          root.remove();
          html.classList.remove('part-search-active');
        }
      } finally {
        if (inflight === controller) {
          html.classList.remove('part-search-loading');
          root.removeAttribute('aria-busy');
        }
      }
    }

    function syncUrl() {
      const p = new URLSearchParams(location.search);
      const set = (k, v) => (v && !(k === 'ps_page' && v === 1) ? p.set(k, v) : p.delete(k));
      set('ps_page', state.page);
      set('ps_vendor', state.vendor);
      set('ps_brand', state.brand);
      set('ps_discount', state.discount);
      set('ps_type', state.type);
      set('ps_sort', state.sort);
      history.replaceState(null, '', location.pathname + '?' + p.toString());
    }

    function select(name, label, options, current) {
      const opts = options.map((o) => `<option value="${esc(o.value)}"${o.value === current ? ' selected' : ''}>${esc(o.label)}</option>`).join('');
      return `<label class="part-search__control"><span class="part-search__control-label">${esc(label)}</span>
                <span class="select"><select class="select__select" data-ps="${name}">${opts}</select></span></label>`;
    }

    function facetOptions(list, current) {
      const opts = [{ value: '', label: L.all || 'All' }].concat(
        list.map((f) => ({ value: f.value, label: `${f.label || f.value} (${f.count})` }))
      );
      if (current && !list.some((f) => f.value === current)) opts.push({ value: current, label: current });
      return opts;
    }

    // A filter is shown when it has a choice to make, or is currently in use.
    function facetSelect(name, label, list) {
      const facet = list || [];
      if (facet.length < 2 && !state[name]) return '';
      return select(name, label, facetOptions(facet, state[name]), state[name]);
    }

    // Theme cards in the theme's own grid when available; otherwise our simple cards.
    function grid(results, cards) {
      const useTheme = cards && cards.some(Boolean);
      if (!useTheme) return `<ul class="part-search__grid" role="list">${results.map(card).join('')}</ul>`;
      loadCardAssets();
      const items = results
        .map((item, i) =>
          cards[i]
            ? `<li class="grid__item part-search__theme-item" data-ps-match="${item.match && item.match.field === 'linked' ? esc(item.match.value) : ''}">${cards[i]}</li>`
            : `<li class="grid__item">${card(item).replace(/^\s*<li[^>]*>|<\/li>\s*$/g, '')}</li>`
        )
        .join('');
      return `<ul class="${esc(gridClass)} part-search__theme-grid" role="list">${items}</ul>`;
    }

    function card(item) {
      const src = item.image || cfg.placeholderImage;
      const img = src
        ? `<img src="${esc(sizedImage(src, 400))}" srcset="${esc(sizedImage(src, 200))} 200w, ${esc(sizedImage(src, 400))} 400w, ${esc(sizedImage(src, 600))} 600w" sizes="(min-width: 990px) 25vw, 50vw" alt="" loading="lazy" width="400" height="400">`
        : '<span class="part-search__placeholder" aria-hidden="true"></span>';
      const price = item.compare_at
        ? `<s class="part-search__compare">${esc(money(item.compare_at))}</s> <span class="part-search__sale">${esc(money(item.price))}</span>`
        : esc(money(item.price));
      return `
        <li class="part-search__card">
          <a href="${esc(item.url)}" class="part-search__link">
            <span class="part-search__media${item.image ? '' : ' part-search__media--placeholder'}">${img}</span>
            <span class="part-search__info">
              ${item.part_number ? `<span class="part-search__part">${esc(L.partNumber || 'Part no.')} ${esc(item.part_number)}</span>` : ''}
              <span class="part-search__title">${esc(item.title)}</span>
              ${matchNote(item)}
              <span class="part-search__price">${price}</span>
              ${item.available ? '' : `<span class="part-search__badge">${esc(L.soldOut || 'Sold out')}</span>`}
            </span>
          </a>
        </li>`;
    }

    function render(data, cards) {
      const pages = Math.max(Math.ceil(data.total / data.per_page), 1);
      if (state.page > pages) state.page = pages;
      const countText = data.total
        ? fill(data.capped ? L.resultsCountCapped : L.resultsCount, { count: data.total, q: esc(state.q) })
        : '';

      const filtering = state.vendor || state.brand || state.type || state.discount;
      const controls = data.total || filtering
        ? `<div class="part-search__controls">
             ${facetSelect('brand', L.brand || 'Brand', data.facets.brand)}
             ${facetSelect('type', L.type || 'Part type', data.facets.type)}
             ${facetSelect('discount', L.discount || 'Discount', data.facets.discount)}
             ${cfg.showSupplierFilter === false ? '' : facetSelect('vendor', L.supplier || 'Supplier', data.facets.vendor)}
             ${select('sort', L.sort || 'Sort by', [
               { value: '', label: L.sortRelevance || 'Best match' },
               { value: 'price_asc', label: L.sortPriceAsc || 'Price, low to high' },
               { value: 'price_desc', label: L.sortPriceDesc || 'Price, high to low' },
               { value: 'part_number', label: L.sortPart || 'Part number, A to Z' },
               { value: 'title', label: L.sortTitle || 'Name, A to Z' },
             ], state.sort)}
           </div>`
        : '';

      const pager = pages > 1
        ? `<nav class="part-search__pager" aria-label="Pagination">
             <button type="button" class="button button--secondary" data-ps-page="${state.page - 1}"${state.page <= 1 ? ' disabled' : ''}>${esc(L.previous || 'Previous')}</button>
             <span>${esc(fill(L.page || 'Page {page} of {pages}', { page: state.page, pages }))}</span>
             <button type="button" class="button button--secondary" data-ps-page="${state.page + 1}"${state.page >= pages ? ' disabled' : ''}>${esc(L.next || 'Next')}</button>
           </nav>`
        : '';

      root.innerHTML = `
        ${data.total ? `<p class="part-search__count" role="status">${countText}</p>` : `<div class="part-search__empty" role="status">${noResultsHtml(state.q)}</div>`}
        ${aliasNote(data)}
        ${controls}
        ${data.results.length ? grid(data.results, cards) : ''}
        ${pager}`;

      // "Supersedes X" under the title of theme cards that matched a linked part
      root.querySelectorAll('.part-search__theme-item[data-ps-match]').forEach((li) => {
        const value = li.getAttribute('data-ps-match');
        if (!value) return;
        const heading = li.querySelector('.card__content .card__heading') || li.querySelector('.card__heading');
        if (heading) heading.insertAdjacentHTML('afterend', `<span class="part-search-note part-search-note--card">${esc(L.supersedes || 'Supersedes')} ${esc(value)}</span>`);
      });
    }

    root.addEventListener('change', (e) => {
      const name = e.target.getAttribute('data-ps');
      if (!name) return;
      state[name] = e.target.value;
      state.page = 1;
      load();
    });

    root.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-ps-page]');
      if (!btn || btn.disabled) return;
      state.page = parseInt(btn.getAttribute('data-ps-page'), 10) || 1;
      load().then(() => root.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    });

    load();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initResultsPage);
  else initResultsPage();
})();
